import { BookUserIcon, Building2Icon, Loader2Icon, SearchIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import API from '@/lib/api'
import { cn } from '@/lib/utils'

import { LookupCountrySelect } from './LookupCountrySelect'

export interface LookupResult {
  id?: string
  name: string
  ico?: string | null
  dic?: string | null
  businessId?: string | null
  taxId?: string | null
  address?: {
    address?: string | null
    city?: string | null
    zip?: string | null
    country?: string | null
  }
  email?: string | null
  phone?: string | null
  inContacts?: boolean
}

export interface UnifiedBusinessSearchProps {
  prefix: 'customer' | 'beneficiary' | 'supplier'
  isReadOnly?: boolean
  className?: string
  inputClassName?: string
  autoFocus?: boolean
  /** Zavolá se po výběru výsledku (kontakt nebo rejstřík). */
  onSelect?: (result: LookupResult) => void
  /** Zavolá se, když uživatel napsal jméno a pole opustil bez výběru. */
  onTextCommit?: (value: string) => void
}

const DEBOUNCE_MS = 400
const MIN_SEARCH_LENGTH = 2

function isIcoQuery(value: string): boolean {
  const trimmed = value.trim()
  return /^\d{7,8}$/.test(trimmed)
}

export const UnifiedBusinessSearch = ({
  prefix,
  isReadOnly = false,
  className,
  inputClassName,
  autoFocus,
  onSelect,
  onTextCommit,
}: UnifiedBusinessSearchProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'participant.form' })
  const { t: tAres } = useTranslation(undefined, { keyPrefix: 'ares' })
  const { setValue, watch } = useFormContext()
  const name = watch(`${prefix}.name`)
  const [searchValue, setSearchValue] = useState('')
  const [lookupCountry, setLookupCountry] = useState('CZ')
  const [results, setResults] = useState<LookupResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchPerformed, setSearchPerformed] = useState(false)
  const [registersSearchClicked, setRegistersSearchClicked] = useState(false)
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openedAtRef = useRef<number>(0)
  const lastSearchRef = useRef<{ query: string; country: string } | null>(null)

  const fetchLookup = useCallback(
    async (query: string, country: string, all = false) => {
      const trimmed = query.trim()
      if (!trimmed) {
        setResults([])
        setSearchPerformed(false)
        return
      }
      const isIco = isIcoQuery(trimmed)
      const params = isIco
        ? { ico: trimmed, country, ...(all && { all: true }) }
        : { name: trimmed, country, ...(all && { all: true }) }
      if (!isIco && trimmed.length < MIN_SEARCH_LENGTH) {
        setResults([])
        setSearchPerformed(false)
        return
      }
      setIsLoading(true)
      setSearchPerformed(false)
      // Reset registers search state when performing a new search (not "all")
      if (!all) {
        setRegistersSearchClicked(false)
      }
      try {
        const response = await API.get<LookupResult | LookupResult[]>(
          '/api/v2/lookup',
          { params },
        )
        const data = response.data
        const list = Array.isArray(data) ? data : [data]
        setResults(list)
        setHighlightedIndex(0)
      } catch (error: unknown) {
        const err = error as { response?: { data?: { status?: number; code?: number }; status?: number } }
        const status = err.response?.status ?? err.response?.data?.status
        const code = err.response?.data?.code
        if (status === 404) {
          toast.error(tAres('notFound'))
        } else if (code === 50302) {
          toast.error(tAres('temporarilyUnavailable'))
        } else {
          toast.error(tAres('unknownError'))
        }
        setResults([])
      } finally {
        setIsLoading(false)
        setSearchPerformed(true)
        lastSearchRef.current = { query: trimmed, country }
      }
    },
    [tAres],
  )

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const trimmed = searchValue.trim()
    if (!trimmed) {
      // Don't clear results when searchValue is empty - we want to preserve them
      // for when the user refocuses the field
      return
    }
    const isIco = isIcoQuery(trimmed)
    if (!isIco && trimmed.length < MIN_SEARCH_LENGTH) {
      setResults([])
      setSearchPerformed(false)
      return
    }
    // Don't re-fetch if we already searched for this exact query and country
    if (
      lastSearchRef.current &&
      lastSearchRef.current.query === trimmed &&
      lastSearchRef.current.country === lookupCountry
    ) {
      return
    }
    debounceRef.current = setTimeout(() => {
      fetchLookup(searchValue, lookupCountry)
    }, DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchValue, lookupCountry, fetchLookup])

  const selectResult = useCallback(
    (result: LookupResult) => {
      onSelect?.(result)
      setValue(`${prefix}.name`, result.name, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue(`${prefix}.businessId`, result.businessId ?? result.ico ?? '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue(`${prefix}.taxId`, result.taxId ?? result.dic ?? '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      if (result.id) {
        setValue(`${prefix}.id`, result.id, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
      }
      setValue(`${prefix}.address.address`, result.address?.address ?? '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue(`${prefix}.address.city`, result.address?.city ?? '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue(`${prefix}.address.zip`, result.address?.zip ?? '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      if (result.address?.country) {
        setValue(`${prefix}.address.country`, result.address?.country, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
      setValue(`${prefix}.email`, result.email ?? '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue(`${prefix}.phone`, result.phone ?? '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setOpen(false)
      setSearchValue('')
      setResults([])
      setSearchPerformed(false)
      setRegistersSearchClicked(false)
      lastSearchRef.current = null
    },
    [prefix, setValue, onSelect],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => (i + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => (i - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      selectResult(results[highlightedIndex])
    }
  }

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`)
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex, open])

  const handleOpenChange = (next: boolean) => {
    if (next) {
      if (!(name ?? '').trim() && !searchPerformed) return
      openedAtRef.current = Date.now()
      setOpen(true)
      if ((name ?? '').trim()) setSearchValue(name ?? '')
    } else {
      if (Date.now() - openedAtRef.current < 300) return
      // Save the typed value to the form field when closing
      if (searchValue.trim() && searchValue !== name) {
        onTextCommit?.(searchValue)
        setValue(`${prefix}.name`, searchValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
      }
      setOpen(false)
      setSearchValue('')
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div className="relative" onKeyDown={handleKeyDown}>
          <InputGroup className={className}>
            <InputGroupInput
              className={cn('w-full px-2', inputClassName)}
              placeholder={t('nameOrBusinessId.placeholder')}
              autoComplete="off"
              autoFocus={autoFocus}
              readOnly={isReadOnly}
              value={open ? searchValue : name}
              onChange={(e) => {
                const v = e.target.value
                setSearchValue(v)
                if (v.trim()) setOpen(true)
              }}
              onFocus={() => handleOpenChange(true)}
            />
            <InputGroupAddon align="inline-end" className="pr-1.5">
              <LookupCountrySelect
                value={lookupCountry}
                onChange={setLookupCountry}
              />
            </InputGroupAddon>
          </InputGroup>
          <div className="absolute right-20 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
            {isLoading ? (
              <Loader2Icon className="size-5 animate-spin" />
            ) : (
              <SearchIcon className="size-5" />
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col max-h-[320px] overflow-hidden">
          <div ref={listRef} className="overflow-auto flex-1">
            {results.length === 0 && !isLoading && searchPerformed && (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Nenalezeno
              </div>
            )}
            {results.length > 0 && (
              <div className="flex flex-col gap-0.5 py-2">
                {results.map((result, index) => (
                  <Button
                    key={result.id ?? `${result.name}-${result.ico ?? index}`}
                    variant="ghost"
                    className="flex items-start gap-2 justify-start h-auto py-2 px-3 rounded-none"
                    data-index={index}
                    data-highlighted={index === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => selectResult(result)}
                  >
                    {result.inContacts ? (
                      <BookUserIcon className="size-4 shrink-0 mt-0.5 text-muted-foreground" />
                    ) : (
                      <Building2Icon className="size-4 shrink-0 mt-0.5 text-muted-foreground" />
                    )}
                    <div className="flex flex-col gap-0.5 text-left min-w-0">
                      <span className="font-medium truncate">{result.name}</span>
                      <div className="flex flex-wrap gap-x-3 gap-y-0 text-xs text-muted-foreground">
                        {result.ico && <span>IČ: {result.ico}</span>}
                        {(result.dic ?? result.taxId) && (
                          <span>DIČ: {result.dic ?? result.taxId}</span>
                        )}
                        {result.address?.city && (
                          <span>{result.address.city}</span>
                        )}
                      </div>
                      {result.inContacts && (
                        <span className="text-xs text-muted-foreground">
                          V kontaktech
                        </span>
                      )}
                    </div>
                  </Button>
                ))}
                {results.some((r) => r.inContacts) && !registersSearchClicked && (
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 justify-start h-auto py-2 px-3 rounded-none text-muted-foreground text-sm border-t mt-1 pt-2"
                    onClick={() => {
                      setRegistersSearchClicked(true)
                      fetchLookup(searchValue, lookupCountry, true)
                    }}
                  >
                    <Building2Icon className="size-4 shrink-0" />
                    Vyhledat v obchodních rejstřících
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

UnifiedBusinessSearch.displayName = 'UnifiedBusinessSearch'
